<template>
  <!-- Both payment options side by side: each tier's range and unit price,
       and the total for `seats`. Without a price (the call failed) nothing is
       shown: no amount is ever made up. -->
  <div v-if="price" class="flex flex-col gap-2 text-sm">
    <div class="overflow-x-auto">
      <table class="w-full border-collapse text-left">
        <caption class="sr-only">
          {{ t("licensing.price.tableCaption") }}
        </caption>
        <thead>
          <tr class="text-xs text-muted-text dark:text-muted-text-dark">
            <th scope="col" class="py-2 pr-3 font-medium">
              {{ t("licensing.price.users") }}
            </th>
            <th
              v-for="option in PAYMENT_OPTIONS"
              :key="option"
              scope="col"
              class="py-2 px-3 font-medium text-right"
              :class="columnClass(option)"
            >
              {{ t(`licensing.price.column.${option}`) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(tier, index) in tiers"
            :key="tier.fromSeats"
            class="border-t border-container-border dark:border-container-border-dark"
            :class="
              seats >= tier.fromSeats
                ? 'text-heading dark:text-heading-dark'
                : 'text-muted-text dark:text-muted-text-dark'
            "
          >
            <th scope="row" class="py-1.5 pr-3 font-normal whitespace-nowrap">
              {{ rangeLabel(tier) }}
            </th>
            <td
              v-for="option in PAYMENT_OPTIONS"
              :key="option"
              class="py-1.5 px-3 text-right tabular-nums whitespace-nowrap"
              :class="columnClass(option)"
            >
              {{ unitPrice(option, index) }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr
            class="border-t-2 border-container-border dark:border-container-border-dark text-heading dark:text-heading-dark font-medium"
          >
            <th scope="row" class="py-2 pr-3 font-medium">
              {{ t("licensing.price.totalFor", seats) }}
            </th>
            <td
              v-for="option in PAYMENT_OPTIONS"
              :key="option"
              class="py-2 px-3 text-right tabular-nums whitespace-nowrap"
              :class="columnClass(option)"
            >
              {{
                t("licensing.price.perInterval", {
                  amount: periodTotal(option, seats),
                  interval: intervalLabel(option),
                })
              }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <p class="text-xs text-muted-text dark:text-muted-text-dark">
      {{ t("licensing.price.cumulative") }}
      <template v-if="!taxIncluded">{{ t("licensing.price.vatNote") }}</template>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { Auth, Org } from "tf-api-client";
import { useSeatPrice } from "../../composables/use-seat-price";
import { PAYMENT_OPTIONS } from "../../config/licensing";
import i18nUtil from "../../utils/i18n";

const props = defineProps<{
  orgId: string | null;
  // the seats to total
  seats: number;
  // the payment option chosen, highlighted
  selected?: Org.SubscriptionBillingInterval | null;
}>();

const { t } = useI18n();
const { price, listFor, intervalLabel, format, periodTotal, taxIncluded } =
  useSeatPrice(() => props.orgId);

// both options share the tier ranges; only the unit prices differ
const tiers = computed(() => price.value?.monthly.tiers ?? []);

const unitPrice = (option: Org.SubscriptionBillingInterval, index: number) => {
  const tier = listFor(option)?.tiers[index];
  return tier ? format(tier.unitAmount) : "";
};

const rangeLabel = (tier: Auth.SeatPriceTier) =>
  tier.toSeats === null
    ? t("licensing.price.tierFrom", { from: i18nUtil.formatNumber(tier.fromSeats) })
    : t("licensing.price.tierRange", {
        from: i18nUtil.formatNumber(tier.fromSeats),
        to: i18nUtil.formatNumber(tier.toSeats),
      });

const columnClass = (option: Org.SubscriptionBillingInterval) =>
  option === props.selected ? "bg-main-button-bg/10" : "";
</script>
